import type React from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { changeCurrentPassword } from "../features/auth/authAPI";

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPasswordMismatch =
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    newPassword !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error("All password fields are required.");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password must match.");
      return;
    }

    try {
      setIsSubmitting(true);
      await changeCurrentPassword({
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      toast.success("Password changed successfully.");
      navigate("/profile-setting?tab=personal");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to change password. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="w-full max-w-3xl mx-auto bg-white rounded-md border border-gray-200 shadow-sm p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Change Password</h1>
          <button
            type="button"
            onClick={() => navigate("/profile-setting?tab=personal")}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
          >
            Back
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-base font-medium text-[#000000] mb-2">
              old_password*
            </label>
            <input
              type="password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
              autoComplete="current-password"
              required
            />
          </div>

          <div>
            <label className="block text-base font-medium text-[#000000] mb-2">
              new_password*
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 ${
                isPasswordMismatch
                  ? "border border-red-500 focus:ring-red-400"
                  : "border border-transparent focus:ring-lime-400"
              }`}
              autoComplete="new-password"
              required
            />
          </div>

          <div>
            <label className="block text-base font-medium text-[#000000] mb-2">
              confirm_password*
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 ${
                isPasswordMismatch
                  ? "border border-red-500 focus:ring-red-400"
                  : "border border-transparent focus:ring-lime-400"
              }`}
              autoComplete="new-password"
              required
            />
            {isPasswordMismatch && (
              <p className="text-red-500 text-sm mt-1">
                New password and confirm password must match.
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={
                isSubmitting ||
                isPasswordMismatch ||
                !oldPassword ||
                !newPassword ||
                !confirmPassword
              }
              className="btn-cta px-8 py-2 font-semibold rounded-md disabled:opacity-60"
            >
              {isSubmitting ? "Submitting..." : "Change Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
